/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HttpPolicySetModule
 */

import {
    Component,
    Type,
} from '@angular/core';

import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import { HTTPSecurityActionType, IHTTPSecurityRule } from 'generated-types';
import { HTTPSecurityRule } from 'object-types';
import { L10nService } from '@vmw/ngx-vip';
import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';
import { SchemaService } from 'ajs/modules/core/services/schema-service/schema.service';
import { DefaultValues } from 'ajs/modules/core/services/default-values.service';
import { MatchTargetConfigItem }
    from 'ajs/modules/policies/factories/config-items/match-target.config-item.factory';

import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import { HTTPSecurityActionConfigItem } from './http-security-action.config-item.factory';
import * as l10n from '../http-policy-set.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type THTTPSecurityRulePartial = Omit<IHTTPSecurityRule, 'match' | 'action'>;

interface IHTTPSecurityRuleConfig extends THTTPSecurityRulePartial {
    match?: MatchTargetConfigItem;
    action?: HTTPSecurityActionConfigItem;
}

/**
 * @description
 *
 *   HTTPPolicySet security rules Message Item.
 *
 * @author Rohit Gaikwad
 */
export class HTTPSecurityRuleConfigItem extends
    withFullModalMixin(MessageItem)<IHTTPSecurityRuleConfig> {
    public static ajsDependencies = [
        'schemaService',
        'l10nService',
    ];

    private readonly schemaService: SchemaService;
    private readonly l10nService: L10nService;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: HTTPSecurityRule,
            windowElement: 'lazy-load',
            ...args,
        };

        super(extendedArgs);
        this.schemaService = this.getAjsDependency_('schemaService');
        this.l10nService = this.getAjsDependency_('l10nService');
    }

    /**
     * Import Lazy Loaded Modal Component.
     */
    /* eslint-disable-next-line */
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const { HTTPPolicySetSecurityRuleModalComponent } = await import(
            /* webpackChunkName: "http-policy-set" */
            // eslint-disable-next-line max-len
            'ng/lazy-loaded-components/modals/http-policy-set-modal/components/http-policy-set-security-rule-modal/http-policy-set-security-rule-modal.component'
        );

        return HTTPPolicySetSecurityRuleModalComponent as Type<Component>;
    }

    /**
     * Returns AuthenticationAction type in human readable format.
     */
    public get securityActionType(): string {
        if (!this.config.action.config.action) {
            return '';
        }

        return this.schemaService.getEnumValueDescription(
            'HTTPSecurityActionType',
            this.config.action.config.action,
        );
    }

    /**
     * Getter for redirect action.
     */
    public get redirectAction() : any {
        const { action } = this.config;

        return action.rateLimiterAction;
    }

    /**
     * Return true if rule has any action configured.
     */
    public hasAnyAction(): boolean {
        return Boolean(this.config.action.config.action);
    }

    /**
     * Set config action as HTTPSecurityAction type
     */
    public setActionValue(actionValue: HTTPSecurityActionType): void {
        const { action } = this.config;

        action.config.action = actionValue;
    }

    /**
     * Delete action from config item.
     */
    public clearAction(): void {
        delete this.config.action.config.action;
    }

    /**
     * @override
     */
    // eslint-disable-next-line no-underscore-dangle
    public get defaultConfigOverride_(): Partial<IHTTPSecurityRuleConfig> {
        const defaultValues: DefaultValues = this.getAjsDependency_('defaultValues');
        const type = this.objectType.toLowerCase();
        const defaultConfig = defaultValues.getDefaultItemConfigByType(type) || {};
        const { match, ...defaults } = defaultConfig;

        return {
            ...defaults,
            match: null,
        };
    }

    /**
     * @override
     */
    protected getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.httpSecurityRuleModalBreadcrumbTitle);
    }
}
